import * as vp from 'vopay';
import { ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import styles from './ExpandCard.module.scss';
import { Button, CircleButton } from 'UI';
import { motion, Easing } from 'motion/react';

const { withStyles } = vp;
const cn = withStyles(styles);

type ExpandCardProps = {
  top: ReactNode | ReactNode[];
  contents: ReactNode;
  buttonText: string;
  align?: 'start' | 'center' | 'end';
  disabled: boolean;
  showButton?: boolean;
  onMouseOver: () => void;
  onMouseOut: () => void;
  onNavigateClick: () => void;
};

type TransitionConfig = {
  duration: number;
  times: number[];
  ease: Easing[];
};

export default function ExpandCard({
  top,
  contents,
  buttonText,
  align = 'start',
  disabled,
  showButton = true,
  onMouseOver,
  onMouseOut,
  onNavigateClick,
}: ExpandCardProps) {
  const internalRef = useRef<HTMLDivElement>(null);

  const [height, setHeight] = useState('100px');
  const [hovering, setHovering] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1200);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1200);
      setHovering(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const el = internalRef.current;
    if (!el) return;

    setHeight(el.scrollHeight + 'px');
    const obs = new ResizeObserver(() => {
      setHeight(el.scrollHeight + 'px');
    });

    obs.observe(el);
    return () => {
      obs.disconnect();
    };
  }, [hovering]);

  const handleMouseOver = useCallback(() => {
    if (!isDesktop) return;
    setHovering(true);
    onMouseOver?.();
  }, [isDesktop, onMouseOver]);

  const handleMouseOut = useCallback(() => {
    if (!isDesktop) return;
    setHovering(false);
    onMouseOut?.();
  }, [isDesktop, onMouseOut]);

  const handleCardClick = useCallback(() => {
    if (isDesktop) {
      if (showButton) {
        onNavigateClick?.();
      }
      return;
    }
    if (!hovering) {
      setHovering(true);
      onMouseOver?.();
    }
  }, [isDesktop, hovering, onNavigateClick, onMouseOver, showButton]);

  const handleClose = useCallback(() => {
    if (!isDesktop && hovering) {
      setHovering(false);
      onMouseOut?.();
    }
  }, [isDesktop, hovering, onMouseOut]);

  const transitionConfig: TransitionConfig = {
    duration: 0.5,
    times: [0, 0.9, 1],
    ease: ['easeInOut', 'easeOut'],
  };

  return (
    <>
      <motion.div
        className={cn('backdrop', hovering && 'hovering', disabled && 'disabled')}
        animate={{
          opacity: hovering ? [0, 0, 1] : 0,
          backgroundColor: hovering ? 'rgba(251, 251, 251, 0.6)' : 'rgba(251, 251, 251, 0)',
          transition: transitionConfig,
        }}
      />
      <motion.div
        key={isDesktop ? 'desktop' : 'mobile'}
        ref={internalRef}
        className={cn(
          'expandCard',
          `align-${align}`,
          !isDesktop && hovering && 'expanded',
          disabled && 'disabled',
          !showButton && 'nonNavigable',
        )}
        style={{ '--scrollHeight': height }}
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseOut}
        onClick={handleCardClick}
        role={showButton ? 'button' : undefined}
        tabIndex={showButton ? 0 : undefined}
        initial={isDesktop ? { width: '100%', height: 'var(--cardHeight)' } : { width: '100%' }}
        whileHover={
          isDesktop
            ? {
                x: align === 'center' ? [null, '-5%', '-20%'] : undefined,
                width: ['100%', '110%', '150%'],
                height: ['var(--cardHeight)', 'var(--cardHeight)', 'var(--scrollHeight)'],
                transition: transitionConfig,
              }
            : undefined
        }
        transition={{
          duration: 0.1,
          ease: 'easeOut',
        }}
      >
        <div className={cn('closeButton', !isDesktop && hovering && 'visible')}>
          <CircleButton icon="close" size="large" circleFill onClick={handleClose} />
        </div>

        <div className={cn('top')}>{top}</div>

        <div className={cn('moreInfo')}>
          {contents}
          {showButton && (
            <Button onClick={onNavigateClick} style={{ marginTop: isDesktop ? 24 : 16 }}>
              {buttonText}
            </Button>
          )}
        </div>
      </motion.div>
    </>
  );
}
